/* eslint-disable max-lines-per-function */
import { useParams } from 'react-router';

import { ControlsBar, Heading, Redirect, Scroll, StyledFlex, useLocationWithBackState } from '@dreryk/react-components';
import { createTranslatedText } from '@dreryk/react-common';

import { ReceptionServicesDetails } from '~libs.shared.reception-service';
import {
   VideoConsultationBackAction,
   VideoConsultationButton,
   VideoConsultationUrlClipboard,
} from '~libs.desktop.video-consultation';
import { patientPaths } from '~libs.desktop.shared.paths';

import { PatientActionBar } from '../common/PatientActionBar';
import { PatientFileLocationData } from '../common/common.types';
import { patientTranslationKeys } from '../translations';

import { PatientUnconfirmedUpdatePanelLoader } from './PatientUnconfirmedUpdatePanel.loader';
import { PatientUnconfirmedAssociateAction } from './PatientUnconfirmedAssociateAction';
import { PatientUnconfirmedForm } from './PatientUnconfirmedForm';
import { PatientUnconfirmedInfoStatus } from './PatientUnconfirmedInfoStatus';
import {
   appointmentVisitor,
   encounterModeVisitor,
   getPatientName,
   getVideoConsultationPatientTemporaryDataLocation,
} from './PatientUnconfirmedUpdatePanel.utils';
import { PatientUnconfirmedUpdateAndStartVisitAction } from './PatientUnconfirmedUpdateAndStartVisitAction';
import { PatientUnconfirmedUpdateAction } from './PatientUnconfirmedUpdateAction';
import { AppointmentDateTime } from './temporaryControlsBar/AppointmentDateTime';
import { AppointmentEncounterModeDropdown } from './temporaryControlsBar/encounterModeDropdown/AppointmentEncounterModeDropdown';

type PatientUnconfirmedUpdatePanelParams = {
   patientId?: string;
};

export const PatientUnconfirmedUpdatePanel = (): JSX.Element => {
   const params = useParams<PatientUnconfirmedUpdatePanelParams>();

   const location = useLocationWithBackState<PatientFileLocationData>();
   const appointmentId = location?.state.data?.appointmentId;

   return (
      <PatientUnconfirmedUpdatePanelLoader patientId={Number(params.patientId)} appointmentId={appointmentId}>
         {({ patient, appointment }) => {
            if (patient.referenceIdentifier) {
               return <Redirect path={patientPaths.getDataPath(patient.id)} />;
            }

            return (
               <PatientActionBar>
                  <Heading
                     type='h1'
                     testId={'patientName'}
                     margin={4}
                     content={createTranslatedText(getPatientName(patient))}
                     bold
                  />
                  <StyledFlex direction={'column'} gap={22}>
                     <StyledFlex direction={'column'} gap={12}>
                        {appointmentVisitor(appointment)({
                           withAppointment: (appointmentDetails) => (
                              <>
                                 <ReceptionServicesDetails appointmentId={appointmentDetails.id} />
                                 <ControlsBar testId={'appointment'}>
                                    <ControlsBar.Main position={'left'}>
                                       <AppointmentDateTime
                                          beginDate={appointmentDetails.beginDate}
                                          endDate={appointmentDetails.endDate}
                                       />
                                       <AppointmentEncounterModeDropdown
                                          encounterMode={appointmentDetails.encounterMode}
                                          appointmentId={appointmentDetails.id}
                                       />
                                    </ControlsBar.Main>

                                    <ControlsBar.Supplementary position={'right'}>
                                       {encounterModeVisitor(appointmentDetails)({
                                          visitVideoConsultation: () => (
                                             <>
                                                <VideoConsultationBackAction
                                                   appointmentId={appointmentDetails.id}
                                                   toOriginLabel={
                                                      patientTranslationKeys.goBackToMedicalRecordFromVideoCall
                                                   }
                                                   label={patientTranslationKeys.goBackToPatientData}
                                                   location={getVideoConsultationPatientTemporaryDataLocation(
                                                      patient.id,
                                                      appointmentDetails.id
                                                   )}
                                                />
                                                <VideoConsultationUrlClipboard
                                                   appointmentId={appointmentDetails.id}
                                                   label={patientTranslationKeys.copyVideoConsultationLinkForPatient}
                                                />
                                                <VideoConsultationButton
                                                   appointmentId={appointmentDetails.id}
                                                   label={patientTranslationKeys.connectWithPatient}
                                                />
                                             </>
                                          ),
                                          visitOnSite: () => null,
                                       })}
                                    </ControlsBar.Supplementary>
                                 </ControlsBar>
                              </>
                           ),
                           withoutAppointment: () => null,
                        })}
                     </StyledFlex>
                     <PatientUnconfirmedInfoStatus unconfirmedPatient={patient} />
                     <Scroll testId={'unconfirmedPatient'}>
                        <PatientUnconfirmedForm patient={patient}>
                           {({ handleSubmit, reset, trigger }) =>
                              appointmentVisitor(appointment)({
                                 withAppointment: (appointmentDetails) => (
                                    <PatientUnconfirmedUpdateAndStartVisitAction
                                       appointmentId={appointmentDetails.id}
                                       patientId={patient.id}
                                       handleSubmit={handleSubmit}
                                       onSuccess={reset}
                                       trigger={trigger}
                                    />
                                 ),
                                 withoutAppointment: () => (
                                    <PatientUnconfirmedUpdateAction
                                       patientId={patient.id}
                                       handleSubmit={handleSubmit}
                                       onSuccess={reset}
                                    />
                                 ),
                              })
                           }
                        </PatientUnconfirmedForm>
                        <PatientUnconfirmedAssociateAction unconfirmedPatient={patient} />
                     </Scroll>
                  </StyledFlex>
               </PatientActionBar>
            );
         }}
      </PatientUnconfirmedUpdatePanelLoader>
   );
};
